import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { MtGroup, MtUser } from "server/models"
import { deepCopy } from "client/utils"
import {
    StoreType,
    UserFilters,
    GroupFilters,
    SortUsers,
    SortGroups,
    setAccountPermissionsUserIds,
    setAccountPermissionsGroupIds,
    setAccountPermissionsUserSort,
    setAccountPermissionsGroupSort,
    setAccountPermissionsUserFilters,
    setAccountPermissionsGroupFilters,
    setAccountPermissionsUserSelected,
    setAccountPermissionsGroupSelected,
    updateAccountPermission,
    closeModal,
} from "client/store"
import {
    PermissionType,
    AccountPermissionItem,
} from "client/store/utils"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    TableSearchWrapper,
    Table,
    SortRows,
    PageForm,
    Button,
} from "client/components"

import UserSearch from "client/pages/search/userSearch"
import GroupSearch from "client/pages/search/groupSearch"
import UserColumns from "./userColumns"
import GroupColumns from "./groupColumns"

const GROUPS = "groups"
const USERS = "users"

interface NotAssignedProps {
    accountId: number
    section: string
    permissionId: string
    permissionType: PermissionType
    permissionItem: AccountPermissionItem
}

const NotAssigned = ({
    accountId = null,
    section,
    permissionId,
    permissionType,
    permissionItem,
}: NotAssignedProps) => {
    const groupList = useSelector((state: StoreType) => state.group.groupList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)

    const groupIds = useSelector((state: StoreType) => state.accountPermissions.groups.groupIds)
    const groupSortColumn = useSelector((state: StoreType) => state.accountPermissions.groups.sortColumn)
    const groupSortOrder = useSelector((state: StoreType) => state.accountPermissions.groups.sortOrder)
    const groupFilters = useSelector((state: StoreType) => state.accountPermissions.groups.filters)
    const groupSelected = useSelector((state: StoreType) => state.accountPermissions.groups.selected)

    const userIds = useSelector((state: StoreType) => state.accountPermissions.users.userIds)
    const userSortColumn = useSelector((state: StoreType) => state.accountPermissions.users.sortColumn)
    const userSortOrder = useSelector((state: StoreType) => state.accountPermissions.users.sortOrder)
    const userFilters = useSelector((state: StoreType) => state.accountPermissions.users.filters)
    const userSelected = useSelector((state: StoreType) => state.accountPermissions.users.selected)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(GROUPS)

    useEffect(() => {
        const sortedGroupIds = SortGroups(Object.values(groupList), groupSortColumn, groupSortOrder, groupFilters)
        const notAssignedGroups = sortedGroupIds.filter((id) => !permissionItem.groups.includes(id))
        dispatch(setAccountPermissionsGroupIds(notAssignedGroups))
    }, [groupList, groupSortColumn, groupSortOrder, groupFilters])

    useEffect(() => {
        const sortedUserIds = SortUsers(Object.values(userList), userSortColumn, userSortOrder, userFilters)
        const accountUserIds = accountUserList[accountId]
        const notAssignedUsers = sortedUserIds.filter((id) => (accountUserIds
            ? accountUserIds.includes(id) && !permissionItem.users.includes(id)
            : false))
        dispatch(setAccountPermissionsUserIds(notAssignedUsers))
    }, [userList, accountUserList, userSortColumn, userSortOrder, userFilters])

    const onSortGroups: SortRows = (column: keyof MtGroup, order) => {
        dispatch(setAccountPermissionsGroupSort(column, order))
    }
    const onSortUsers: SortRows = (column: keyof MtUser, order) => {
        dispatch(setAccountPermissionsUserSort(column, order))
    }

    const onGroupFilter = (newFilters: GroupFilters) => {
        dispatch(setAccountPermissionsGroupFilters(newFilters))
        dispatch(setAccountPermissionsGroupSelected(null))
    }
    const onUserFilter = (newFilters: UserFilters) => {
        dispatch(setAccountPermissionsUserFilters(newFilters))
        dispatch(setAccountPermissionsUserSelected(null))
    }

    const onSelect = () => {
        const newPermissionItem: AccountPermissionItem = deepCopy(permissionItem)
        const { groups, users } = newPermissionItem
        newPermissionItem.groups = groups.concat(groupSelected)
        newPermissionItem.users = users.concat(userSelected)

        dispatch(updateAccountPermission(
            section,
            permissionId,
            permissionType,
            newPermissionItem,
        ))

        dispatch(closeModal())
        dispatch(setAccountPermissionsGroupSelected(null))
        dispatch(setAccountPermissionsUserSelected(null))
    }

    const userColumns = UserColumns()
    const groupColumns = GroupColumns(accountId)

    return (
        <Container>
            <PageHeader>
                <PageTitle>{activeTab === GROUPS ? lang.selectGroup_s : lang.selectUser_s}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === GROUPS}
                        onClick={() => setActiveTab(GROUPS)}
                    >
                        {lang.groups}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === USERS}
                        onClick={() => setActiveTab(USERS)}
                    >
                        {lang.users}
                    </PageMenuButton>
                </PageMenu>

                <Tab show={activeTab === GROUPS} noGrow>
                    <TableSearchWrapper>
                        <GroupSearch
                            id="permissionGroupList"
                            filters={groupFilters}
                            onFilter={onGroupFilter}
                        />
                    </TableSearchWrapper>
                    <Table
                        columns={groupColumns}
                        dataList={groupList}
                        dataIds={groupIds}
                        selectedIds={groupSelected}
                        sortColumn={groupSortColumn}
                        sortOrder={groupSortOrder}
                        rowSize="sm"
                        showPagination
                        onSort={onSortGroups}
                    />
                </Tab>

                <Tab show={activeTab === USERS} noGrow>
                    <TableSearchWrapper>
                        <UserSearch
                            id="permissionUserList"
                            filters={userFilters}
                            onFilter={onUserFilter}
                        />
                    </TableSearchWrapper>
                    <Table
                        columns={userColumns}
                        dataList={userList}
                        dataIds={userIds}
                        selectedIds={userSelected}
                        sortColumn={userSortColumn}
                        sortOrder={userSortOrder}
                        rowSize="sm"
                        showPagination
                        onSort={onSortUsers}
                    />
                </Tab>

                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <Button variant="success" row onClick={() => onSelect()}>
                                {lang.select}
                            </Button>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default NotAssigned
